import React from 'react';
import {
    Apple,
    Bed,
    Beer,
    Bell,
    Cake,
    Car,
    ChefHat,
    Coffee,
    CupSoda,
    Drumstick,
    Fish,
    Fuel,
    Gift,
    Heart,
    House,
    LucideIcon,
    Martini,
    Music,
    Plane,
    Shirt,
    ShoppingBag,
    Tag,
    Utensils,
    Wine,
    Zap,
} from 'lucide-react';

export const ITEM_ICON_MAP: Record<string, LucideIcon> = {
    // Food & Drink
    meal: Utensils,
    food: Utensils,
    coffee: Coffee,
    tea: Coffee,
    beer: Beer,
    wine: Wine,
    cocktail: Martini,
    martini: Martini,
    drink: CupSoda,
    soda: CupSoda,
    dining: ChefHat,
    chef: ChefHat,
    meat: Drumstick,
    chicken: Drumstick,
    steak: Drumstick,
    burger: Drumstick,
    sandwich: Drumstick,
    seafood: Fish,
    fish: Fish,
    sushi: Fish,
    dessert: Cake,
    cake: Cake,
    bakery: Cake,
    fruit: Apple,
    apple: Apple,
    produce: Apple,
    groceries: ShoppingBag,
    market: ShoppingBag,

    // Shopping & Entertainment
    shopping: Shirt,
    clothes: Shirt,
    apparel: Shirt,
    gift: Gift,
    delivery: Gift,
    package: Gift,
    amazon: Gift,
    entertainment: Music,
    music: Music,
    concert: Music,
    ticket: Music,
    movie: Music,

    // Travel & Transport
    transport: Car,
    car: Car,
    uber: Car,
    lyft: Car,
    taxi: Car,
    travel: Plane,
    plane: Plane,
    flight: Plane,
    gas: Fuel,
    fuel: Fuel,
    hotel: Bed,
    stay: Bed,
    airbnb: Bed,

    // Bills, Health, Home & Services
    home: House,
    rent: House,
    utilities: Zap,
    power: Zap,
    electric: Zap,
    wifi: Zap,
    health: Heart,
    pharmacy: Heart,
    medical: Heart,
    service: Bell,
    bell: Bell,
    tip: Bell,
    cleaning: Bell,
    fee: Tag,
    tag: Tag,
    discount: Tag,
    receipt: Tag,
    generic: Utensils,
};

export const inferItemIconKey = (name: string): string => {
    const t = (name || '').toLowerCase();
    // Travel & Transport
    if (/(flight|airline|airfare|delta|united|american air|southwest|boarding|luggage|baggage|airport)/.test(t)) return 'travel';
    if (/(uber|lyft|taxi|cab|transit|train|subway|metro|toll|parking|valet|ride)/.test(t)) return 'transport';
    if (/(gas|fuel|shell|chevron|bp|exxon|mobil|ev charge|supercharger)/.test(t)) return 'gas';
    if (/(hotel|airbnb|motel|resort|inn|lodging|hostel|marriott|hilton|hyatt)/.test(t)) return 'hotel';

    // Entertainment & Shopping
    if (/(ticket|concert|movie|cinema|theater|event|admission|festival|show|museum|music|spotify)/.test(t)) return 'entertainment';
    if (/(shirt|clothing|clothes|apparel|shoes|zara|h&m|uniqlo|nordstrom|nike|adidas|dress|pants)/.test(t)) return 'shopping';
    if (/(amazon|package|delivery|shipping|ups|fedex|usps|gift|present)/.test(t)) return 'gift';

    // Health, Home, Utilities & Services
    if (/(pharmacy|cvs|walgreens|medicine|rx|doctor|health|clinic|dental|hospital|gym|fitness)/.test(t)) return 'health';
    if (/(rent|lease|mortgage|furniture|ikea|home depot|lowes)/.test(t)) return 'home';
    if (/(electric|power|utility|utilities|wifi|internet|coned|water bill|pge)/.test(t)) return 'utilities';
    if (/(tip|gratuity|service fee|cleaning|maid|barber|haircut|salon|spa|room service)/.test(t)) return 'service';
    if (/(discount|coupon|promo|fee|surcharge|tax|charge)/.test(t)) return 'fee';

    // Drinks
    if (/(tea|coffee|latte|espresso|cappuccino|matcha|cold brew|starbucks|dunkin)/.test(t)) return 'coffee';
    if (/(beer|ipa|lager|ale|stout|pilsner|cider|draft|brewery)/.test(t)) return 'beer';
    if (/(wine|cab|merlot|pinot|chardonnay|sauvignon|prosecco|champagne|rosé|rose|winery)/.test(t)) return 'wine';
    if (/(cocktail|martini|margarita|aperol|spritz|capi|sour|mojito|negroni|gin|vodka|tequila|rum|whiskey|bourbon|bar)/.test(t)) return 'cocktail';
    if (/(coke|soda|sprite|pepsi|juice|lemonade|beverage|drink|water)/.test(t)) return 'drink';

    // Food
    if (/(fish|shrimp|crab|lobster|salmon|tuna|sushi|sashimi|calamari|oyster|seafood|poke)/.test(t)) return 'seafood';
    if (/(chicken|wing|wings|tender|tenders|drumstick|poultry|turkey|steak|beef|ribeye|sirloin|burger|sandwich|pork|bbq|meat)/.test(t)) return 'meat';
    if (/(cake|dessert|brownie|cheesecake|pastry|pie|tart|tiramisu|ice cream|gelato|sorbet|donut|cookie|bakery)/.test(t)) return 'dessert';
    if (/(fruit|apple|berry|berries|banana|strawberry|citrus|orange|lemon|salad|greens|produce)/.test(t)) return 'fruit';
    if (/(groceries|grocery|market|store|target|walmart|trader joe|costco|whole foods|safeway)/.test(t)) return 'groceries';
    if (/(chef|dining|tasting|bistro|kitchen|restaurant|course)/.test(t)) return 'dining';

    return 'meal';
};

export const getItemIconComponent = (keyOrName?: string): LucideIcon => {
    if (!keyOrName) return Utensils;
    const direct = ITEM_ICON_MAP[keyOrName.toLowerCase()];
    if (direct) return direct;
    const inferred = inferItemIconKey(keyOrName);
    return ITEM_ICON_MAP[inferred] || Utensils;
};

interface WebItemIconAvatarProps {
    name?: string;
    iconKey?: string;
    size?: number; // size in px, default 36
    className?: string;
}

export const WebItemIconAvatar: React.FC<WebItemIconAvatarProps> = ({
    name,
    iconKey,
    size = 36,
    className = '',
}) => {
    const resolvedKey = iconKey || (name ? inferItemIconKey(name) : 'meal');
    const IconComponent = getItemIconComponent(resolvedKey);
    const iconSize = Math.max(14, Math.round(size * 0.46));

    return (
        <div
            className={`rounded-full bg-slate-100 flex items-center justify-center shrink-0 text-slate-600 ${className}`}
            style={{ width: `${size}px`, height: `${size}px` }}
        >
            <IconComponent size={iconSize} fill="currentColor" strokeWidth={1.5} />
        </div>
    );
};
